"use client"

import type { CSSProperties } from "react"
import { getCaptionDisplayModel } from "@/lib/caption-display"
import { resolveCaptionCenter } from "@/lib/caption-layout"
import { displayCaptionText } from "@/lib/caption-utils"
import { convertStyleToConfig } from "@/lib/editingScriptUtils"
import { maskProfanity } from "@/lib/profanity"
import type { CaptionStyle } from "@/types/caption"
import type { CaptionStyleConfig } from "@/remotion/schemas"

/** The composition the caption style is sized for (px). */
const FRAME_WIDTH = 1080
const FRAME_HEIGHT = 1920
const MAX_PREVIEW_WORDS = 3

export interface CardCaption {
  words: string[]
  config: CaptionStyleConfig
  /** The caption's centre as a percentage of the box height. */
  topPct: number
}

/**
 * What a clip card shows of its captions: the first caption group (at most three words) in the clip's
 * caption style, masked as the export masks it, centred where the style puts it on a 9:16 frame that
 * the card shows object-cover in a box of `boxAspect` (width / height). Null without caption text.
 */
export function cardCaption(
  firstCaption: string | null | undefined,
  style: Partial<CaptionStyle> | null | undefined,
  boxAspect: number,
): CardCaption | null {
  const config = convertStyleToConfig({ ...style } as CaptionStyle)
  const words = (firstCaption ?? "").replace(/(\.\.\.|…)$/, "").split(/\s+/).filter(Boolean)
    .slice(0, Math.min(config.wordsPerCaption, MAX_PREVIEW_WORDS))
  if (words.length === 0) return null
  // New clips mask by default (the backend turns it on unless the style says off).
  const masked = style?.maskProfanity !== false
  const shown = words.map((word) => (masked ? maskProfanity(displayCaptionText(word)) : displayCaptionText(word)))
  // object-cover: the frame fills the box width, so its height in box widths is FRAME_HEIGHT / FRAME_WIDTH.
  const { y } = resolveCaptionCenter(config)
  const topInWidths = 1 / (2 * boxAspect) + (y - 0.5) * (FRAME_HEIGHT / FRAME_WIDTH)
  const topPct = Math.min(80, Math.max(12, topInWidths * boxAspect * 100))
  return { words: shown, config: { ...config, maskProfanity: masked }, topPct }
}

/** Composition px as a share of the card's width (cqw), so the caption scales with the card. */
const cqw = (px: number) => `${(px / FRAME_WIDTH) * 100}cqw`

/**
 * The clip's opening caption drawn over its thumbnail as the export draws it: font, size relative to the
 * frame, colours, stroke, active-word colour, case and backdrop. The parent must be `position: relative`
 * with `container-type: inline-size` and `boxAspect` (width / height) its shape.
 */
export function ClipCaptionPreview({ text, style, boxAspect }: {
  text: string | null | undefined
  style: Partial<CaptionStyle> | null | undefined
  boxAspect: number
}) {
  const caption = cardCaption(text, style, boxAspect)
  if (!caption) return null
  const { config, words } = caption
  const display = getCaptionDisplayModel(config, {
    mode: "render", compositionWidth: FRAME_WIDTH, compositionHeight: FRAME_HEIGHT, wordsInGroup: words.length,
  })
  const wordStyle = (active: boolean): CSSProperties => {
    const karaokeBg = active && config.animation === "karaoke-bg"
    return {
      fontFamily: `${config.font.family}, sans-serif`,
      fontSize: cqw(display.fontSize),
      fontWeight: config.font.weight as CSSProperties["fontWeight"],
      fontStyle: config.font.style,
      color: karaokeBg ? display.activeTextColor : active ? display.primaryColor : display.defaultColor,
      backgroundColor: karaokeBg ? display.activeBackgroundColor : undefined,
      borderRadius: karaokeBg ? cqw(8) : undefined,
      padding: karaokeBg ? "0.04em 0.16em" : undefined,
      textTransform: config.textTransform as CSSProperties["textTransform"],
      textShadow: display.textShadow,
      WebkitTextStroke: config.stroke.enabled ? `${cqw(display.strokeWidth)} ${config.stroke.color}` : undefined,
      paintOrder: "stroke fill",
      letterSpacing: display.letterSpacing,
      lineHeight: display.lineHeight,
    }
  }
  return (
    <div
      data-testid="card-caption"
      className="pointer-events-none absolute inset-x-0 z-10 flex -translate-y-1/2 justify-center"
      style={{ top: `${caption.topPct}%` }}
    >
      <div
        className="inline-flex flex-wrap justify-center"
        style={{
          maxWidth: `${display.captionWidthPct}%`,
          gap: `0 ${cqw(display.fontSize * 0.3)}`,
          backgroundColor: display.backdrop.enabled ? display.backdrop.color : undefined,
          borderRadius: display.backdrop.enabled ? cqw(display.backdrop.radius) : undefined,
          padding: display.backdrop.enabled
            ? `${cqw(parseFloat(display.backdrop.paddingY))} ${cqw(parseFloat(display.backdrop.paddingX))}`
            : undefined,
        }}
      >
        {words.map((word, index) => (
          <span key={index} style={wordStyle(index === 0 && config.activeWordEnabled)}>{word}</span>
        ))}
      </div>
    </div>
  )
}
